import * as Dialog from '@radix-ui/react-dialog';
import { useState, type FormEvent, type ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import { Field, FormError, SelectField, TextareaField } from '@/components/ui/field';
import {
  useApproveExpense,
  useCreateAdminExpense,
  useDeclineExpense,
  useUploadAdminReceipt,
} from '@/hooks/expenses';
import { ApiError } from '@/lib/api';
import { useToast } from '@/lib/toast-context';
import { formatCents, parseMoneyInput } from '@shared/money';
import {
  EXPENSE_CATEGORY_LABELS,
  EXPENSE_CATEGORY_VALUES,
  type AdminExpenseView,
} from '@shared/schemas/expense';

/*
 * The three dialogs behind /admin/expenses — ROADMAP.md §10.1.
 *
 * **Money never becomes a float on its way through here.** The input is text,
 * `parseMoneyInput` turns it into integer cents, and that integer is what goes
 * on the wire. `19.99 * 100` is 1998.9999999999998 in IEEE 754 and `Math.round`
 * hides it until the day it does not — `@shared/money` builds the number from
 * the digits instead, and this screen is one of the reasons that file exists.
 */

function Shell({
  open,
  onOpenChange,
  title,
  description,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-ink-900/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 max-h-[calc(100vh-2rem)] w-[min(32rem,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-lg bg-white p-6 shadow-card">
          <Dialog.Title className="text-h2 text-ink-900">{title}</Dialog.Title>
          <Dialog.Description className="mt-1 text-body text-ink-500">
            {description}
          </Dialog.Description>
          <div className="mt-4">{children}</div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// ---------------------------------------------------------------------------
// Recording one
// ---------------------------------------------------------------------------

export function RecordExpenseDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const toast = useToast();
  const create = useCreateAdminExpense();

  const [category, setCategory] = useState<string>('supplies');
  const [amount, setAmount] = useState('');
  const [incurredOn, setIncurredOn] = useState('');
  const [description, setDescription] = useState('');
  const [amountError, setAmountError] = useState<string | undefined>(undefined);

  const apiError = create.error instanceof ApiError ? create.error : null;

  function submit(event: FormEvent) {
    event.preventDefault();

    // Text to integer cents, once, here. Nothing downstream sees a decimal.
    const parsed = parseMoneyInput(amount);
    if (!parsed.ok) {
      setAmountError(parsed.message);
      return;
    }
    if (parsed.cents === null) {
      setAmountError('Enter an amount');
      return;
    }
    setAmountError(undefined);

    create.mutate(
      {
        category: category as (typeof EXPENSE_CATEGORY_VALUES)[number],
        amountCents: parsed.cents,
        incurredOn,
        description: description.trim() === '' ? null : description.trim(),
      },
      {
        onSuccess: (saved) => {
          toast.success(`Expense recorded — ${formatCents(saved.amountCents)}.`);
          setAmount('');
          setDescription('');
          onOpenChange(false);
        },
      },
    );
  }

  return (
    <Shell
      open={open}
      onOpenChange={onOpenChange}
      title="Record an expense"
      description="A business cost with nobody to reimburse. It is recorded as pending, the same as a submitted one."
    >
      <form onSubmit={submit} className="space-y-4">
        <SelectField
          label="Category"
          required
          value={category}
          onChange={(event) => {
            setCategory(event.target.value);
          }}
          options={EXPENSE_CATEGORY_VALUES.map((value) => ({
            value,
            label: EXPENSE_CATEGORY_LABELS[value],
          }))}
        />

        <Field
          label="Amount"
          required
          inputMode="decimal"
          placeholder="125.50"
          hint="Dollars and cents."
          value={amount}
          error={amountError}
          onChange={(event) => {
            setAmount(event.target.value);
          }}
        />

        <Field
          label="Date of the cost"
          required
          type="date"
          value={incurredOn}
          onChange={(event) => {
            setIncurredOn(event.target.value);
          }}
        />

        <TextareaField
          label="What it was for"
          rows={3}
          value={description}
          onChange={(event) => {
            setDescription(event.target.value);
          }}
        />

        {apiError ? <FormError>{apiError.message}</FormError> : null}

        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onOpenChange(false);
            }}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? 'Saving…' : 'Record it'}
          </Button>
        </div>
      </form>
    </Shell>
  );
}

// ---------------------------------------------------------------------------
// Reviewing
// ---------------------------------------------------------------------------

export function ReviewExpenseDialog({
  expense,
  mode,
  onClose,
}: {
  expense: AdminExpenseView | null;
  mode: 'approve' | 'decline';
  onClose: () => void;
}) {
  const toast = useToast();
  const approve = useApproveExpense();
  const decline = useDeclineExpense();
  const [note, setNote] = useState('');

  const mutation = mode === 'approve' ? approve : decline;
  const apiError = mutation.error instanceof ApiError ? mutation.error : null;

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!expense) return;

    if (mode === 'approve') {
      approve.mutate(
        { id: expense.id, input: { reviewNote: note.trim() === '' ? null : note.trim() } },
        {
          onSuccess: () => {
            toast.success(`Approved — ${formatCents(expense.amountCents)}.`);
            setNote('');
            onClose();
          },
        },
      );
      return;
    }

    decline.mutate(
      { id: expense.id, input: { reviewNote: note.trim() } },
      {
        onSuccess: () => {
          toast.success('Declined, and the reason has been recorded.');
          setNote('');
          onClose();
        },
      },
    );
  }

  return (
    <Shell
      open={expense !== null}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={mode === 'approve' ? 'Approve this expense' : 'Decline this expense'}
      description={
        expense
          ? `${formatCents(expense.amountCents)} · ${expense.categoryLabel} · ${expense.employeeName ?? 'No employee'}`
          : ''
      }
    >
      <form onSubmit={submit} className="space-y-4">
        <TextareaField
          label={mode === 'approve' ? 'Note (optional)' : 'Why it is being declined'}
          required={mode === 'decline'}
          rows={3}
          hint={
            mode === 'decline'
              ? 'The person who submitted this reads it. A bare "declined" is a phone call.'
              : undefined
          }
          value={note}
          onChange={(event) => {
            setNote(event.target.value);
          }}
        />

        {apiError ? <FormError>{apiError.message}</FormError> : null}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={mutation.isPending || (mode === 'decline' && note.trim() === '')}
          >
            {mutation.isPending ? 'Saving…' : mode === 'approve' ? 'Approve' : 'Decline'}
          </Button>
        </div>
      </form>
    </Shell>
  );
}

// ---------------------------------------------------------------------------
// Receipts
// ---------------------------------------------------------------------------

export function UploadReceiptDialog({
  expense,
  onClose,
}: {
  expense: AdminExpenseView | null;
  onClose: () => void;
}) {
  const toast = useToast();
  const upload = useUploadAdminReceipt();
  const [file, setFile] = useState<File | null>(null);

  const apiError = upload.error instanceof ApiError ? upload.error : null;

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!expense || !file) return;

    const form = new FormData();
    form.set('receipt', file);

    upload.mutate(
      { id: expense.id, form },
      {
        onSuccess: () => {
          toast.success('Receipt attached.');
          setFile(null);
          onClose();
        },
      },
    );
  }

  return (
    <Shell
      open={expense !== null}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title="Attach a receipt"
      description="A photo of the receipt, or a PDF from the supplier."
    >
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5">
          <label htmlFor="receipt-file" className="block text-label text-ink-900">
            Receipt
            <span aria-hidden="true" className="ml-0.5 text-bark-700">
              *
            </span>
            <span className="sr-only"> (required)</span>
          </label>
          <input
            id="receipt-file"
            name="receipt"
            type="file"
            required
            /*
             * A hint to the file picker and nothing more. **The server decides
             * what a file is from its first bytes** — an `accept` attribute is
             * a convenience for the person choosing, not a check.
             */
            accept="application/pdf,image/*"
            className="min-h-control w-full rounded-sm border border-ink-300 bg-white px-3 py-2 text-body"
            onChange={(event) => {
              setFile(event.target.files?.[0] ?? null);
            }}
          />
          <p className="text-meta text-ink-500">Up to 10MB. PDF, JPEG, PNG, WebP or HEIC.</p>
        </div>

        {apiError ? <FormError>{apiError.message}</FormError> : null}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={upload.isPending || file === null}>
            {upload.isPending ? 'Uploading…' : 'Attach it'}
          </Button>
        </div>
      </form>
    </Shell>
  );
}
